<template>
  <div class="h-full bg-[#f8fafc] p-4 overflow-y-auto custom-scroll">
    <!-- Reports Card -->
    <div class="bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] transition-all overflow-hidden mb-6">
      <div class="px-6 pt-5 pb-3 flex items-center justify-between gap-3">
        <h2 class="text-base font-bold text-[#0d4169]">POS Report</h2>
        <span class="text-xs font-medium text-slate-500 whitespace-nowrap">
          Last Sync: {{ lastPosSyncDisplay }}
        </span>
      </div>

      <form @submit.prevent="generateReport" class="px-6 pb-6 pt-2 border-b border-slate-100">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3 items-start">
          <!-- Start Date (required) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date <span class="text-red-500">*</span></label>
            <input
              v-model="filters.startDate"
              required
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <!-- End Date (optional) -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <input
              v-model="filters.endDate"
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <!-- Report type -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Report type</label>
            <select
              v-model="filters.reportType"
              @change="() => { rows = []; unseenOrderIds = []; pagination.page = 1; pagination.totalPages = 0; pagination.totalCount = 0; }"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option value="Summary">Summary</option>
              <option value="Detail">Detail</option>
            </select>
          </div>

          <!-- Payment Method -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Payment Method</label>
            <select
              v-model="filters.paymentMethodId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option :value="null">All Methods</option>
             <option
  v-for="pm in paymentMethods.filter(pm => pm.name !== 'Delivery')"
  :key="pm.id"
  :value="pm.id"
>
  {{ pm.name }}
</option>
              <option value="Delivery">Delivery</option>
            </select>
          </div>

          <!-- Delivery Company (Conditional) -->
          <div v-if="filters.paymentMethodId === 'Delivery'" class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Delivery Company</label>
            <select
              v-model="filters.deliveryCompanyId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option :value="null">All Companies</option>
              <option v-for="dc in deliveryCompanies" :key="dc.id" :value="dc.id">{{ dc.name }}</option>
            </select>
          </div>

          <!-- By Department -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">By Department</label>
            <select
              v-model="filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option :value="null">All Departments</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>

          <!-- By Sub Department -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">By Sub Department</label>
            <select
              v-model="filters.subDepartmentId"
              :disabled="!filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer disabled:opacity-50 hover:bg-slate-50"
            >
              <option :value="null">All Sub Depts</option>
              <option v-for="sd in subDepartments" :key="sd.id" :value="sd.id">{{ sd.name }}</option>
            </select>
          </div>

          <!-- Select Waiter -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Waiter</label>
            <select
              v-model="filters.waiterId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option :value="null">All Users</option>
              <option v-for="u in allUsers" :key="u.id" :value="u.id">{{ u.full_name || u.username }}</option>
            </select>
          </div>

          <!-- Select Cashier -->
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Select Cashier</label>
            <select
              v-model="filters.cashierId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option :value="null">All Cashiers</option>
              <option v-for="u in cashiers" :key="u.id" :value="u.id">{{ u.full_name || u.username }}</option>
            </select>
          </div>

          <div class="col-span-1 sm:col-span-2 md:col-span-3 lg:col-span-4">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3 items-start">
              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">By Menu Name</label>
                <input
                  v-model="filters.menuName"
                  type="text"
                  placeholder="Search by item name..."
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>

              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#0d4169] flex items-center gap-1">
                  Filter by Management
                  <span class="text-[10px] bg-[#0d4169]/10 text-[#0d4169] px-1 rounded">Staff</span>
                </label>
                <select
                  v-model="filters.managementUserId"
                  class="px-2.5 py-1.5 border border-[#0d4169]/30 rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
                >
                  <option disabled :value="null">Select User</option>
                  <option :value="0">All Staff Users</option>
                  <option v-for="u in managementStaffUsers" :key="u.id" :value="u.id">{{ u.full_name || u.username }}</option>
                </select>
              </div>

              <div class="flex flex-col gap-1 text-left">
                <label class="text-[0.78rem] font-semibold text-[#1e293b]">By Created Date</label>
                <input
                  v-model="filters.createdDate"
                  type="date"
                  class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
                />
              </div>

              <div v-if="unseenPaidOrdersCount > 0" class="flex w-full justify-end self-start sm:col-span-2 lg:col-span-1 lg:col-start-4">
                <span
                  role="button"
                  tabindex="0"
                  class="px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap bg-rose-100 text-rose-700 border border-rose-300 cursor-pointer hover:bg-rose-200 transition-colors"
                  title="Click to view unseen paid orders"
                  @click="openUnseenOrdersModal"
                  @keydown.enter="openUnseenOrdersModal"
                >
                  Unseen Orders: {{ unseenPaidOrdersCount }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end items-center gap-4">
          <div v-if="loading" class="text-xs text-[#0d4169] flex items-center gap-2 font-medium">
            <span class="w-4 h-4 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin"></span>
            Processing...
          </div>
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="!canPrintReport"
            title="Print is available after the report is generated"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button
            type="submit"
            class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading || !filters.startDate"
          >
            <span>Generate Report</span>
          </button>
        </div>
      </form>

      <!-- Results Table Section -->
      <div class="relative overflow-x-auto min-h-[400px] px-3">
        <table class="w-full border-collapse bg-white">
          <thead class="sticky top-0 z-10 bg-[#f8fafc] shadow-sm">
            <!-- Summary Header -->
            <tr v-if="filters.reportType === 'Summary'">
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">No</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Department</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Sub Department</th>
              <th class="px-4 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Menu Name</th>
              <th class="px-4 py-3 text-center font-bold text-[#64748b] border-b text-[11px]">Quantity</th>
              <th class="px-4 py-3 text-right font-bold text-[#64748b] border-b text-[11px]">Unit Price</th>
              <th class="px-4 py-3 text-right font-bold text-[#64748b] border-b text-[11px]">Total Price</th>
            </tr>
            <!-- Detail Header -->
            <tr v-else-if="filters.reportType === 'Detail'">
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">No</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Date</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Department</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Sub Department</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Menu Name</th>
              <th class="px-3 py-3 text-center font-bold text-[#64748b] border-b text-[11px]">Quantity</th>
              <th class="px-3 py-3 text-right font-bold text-[#64748b] border-b text-[11px]">Unit Price</th>
              <th class="px-3 py-3 text-right font-bold text-[#64748b] border-b text-[11px]">Total Price</th>
            </tr>
            <!-- Management Header (New) -->
            <tr v-else-if="filters.reportType === 'Management'">
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">No</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Date and time</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Department</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Sub Department</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">Menu Name</th>
              <th class="px-3 py-3 text-center font-bold text-[#64748b] border-b text-[11px]">Quantity</th>
              <th class="px-3 py-3 text-left font-bold text-[#64748b] border-b text-[11px]">User Name</th>
            </tr>
          </thead>

        <tbody>
          <tr v-if="rows.length === 0">
            <td :colspan="filters.reportType === 'Summary' ? 7 : (filters.reportType === 'Detail' ? 8 : 7)" class="px-4 py-32 text-center text-slate-400 italic text-sm">
              <div class="text-5xl mb-4 opacity-20">📄</div>
              No report data to display.<br/>
              Select filters and click <span class="text-[#0d4169] font-bold">"Generate Report"</span>
            </td>
          </tr>

          <!-- Summary Rows -->
          <template v-if="filters.reportType === 'Summary'">
            <template v-for="(row, index) in rows" :key="index">
              <tr 
                v-if="row.type === 'data'" 
                class="border-b last:border-0 hover:bg-slate-50 transition-colors"
              >
                <td class="px-4 py-3 text-slate-500 text-xs">{{ ((pagination.page - 1) * pagination.limit) + index + 1 }}</td>
                <td class="px-4 py-3 text-left font-medium text-slate-700">{{ row.department }}</td>
                <td class="px-4 py-3 text-left text-slate-600">{{ row.subDepartment }}</td>
                <td class="px-4 py-3 text-left font-semibold text-[#0d4169]">{{ row.menuName }}</td>
                <td class="px-4 py-3 text-center font-bold text-slate-600">{{ row.quantity }}</td>
                <td class="px-4 py-3 text-right font-medium text-slate-600">{{ Number(row.unitPrice).toLocaleString() }}</td>
                <td class="px-4 py-3 text-right font-bold text-[#0d4169]">{{ Number(row.totalPrice).toLocaleString() }}</td>
              </tr>
              
              <tr v-else-if="row.type === 'subtotal'" class="border-b bg-slate-100/50 font-bold">
                <td colspan="4" class="px-4 py-2 text-right text-emerald-600 uppercase tracking-wider text-[9px] font-bold">{{ row.department }} TOTAL</td>
                <td class="px-4 py-2 border-b"></td>
                <td class="px-4 py-2 border-b"></td>
                <td class="px-4 py-2 text-right font-black text-emerald-600 text-sm border-b">{{ Number(row.totalPrice).toLocaleString() }}</td>
              </tr>

              <template v-else-if="row.type === 'total'">
                <!-- Payment Method Breakdown (only for All Methods) -->
                <template v-if="filters.paymentMethodId === null && pagination.paymentMethodTotals?.length">
                  <tr v-for="(pm, pmIdx) in pagination.paymentMethodTotals" :key="pm.name">
                    <td 
                      colspan="6" 
                      :class="['px-4 py-2 text-right text-slate-500 font-bold uppercase text-[10px]', pmIdx === 0 ? 'border-t-2 border-emerald-700/30' : '']"
                    >
                      {{ pm.name }}:
                    </td>
                    <td :class="['px-4 py-2 text-right font-bold text-slate-600 text-sm', pmIdx === 0 ? 'border-t-2 border-emerald-700/30' : '']">
                      {{ Number(pm.amount).toLocaleString() }}
                    </td>
                  </tr>
                </template>

                <tr>
                  <td 
                    colspan="6" 
                    :class="[
                      'px-4 py-4 text-right text-emerald-700 font-extrabold uppercase tracking-widest text-sm',
                      (filters.paymentMethodId !== null || !pagination.paymentMethodTotals?.length) ? 'border-t-2 border-emerald-700/30' : ''
                    ]"
                  >
                    GRAND TOTAL
                  </td>
                  <td 
                    :class="[
                      'px-4 py-4 text-right font-black text-emerald-700 text-lg',
                      (filters.paymentMethodId !== null || !pagination.paymentMethodTotals?.length) ? 'border-t-2 border-emerald-700/30' : ''
                    ]"
                  >
                    {{ Number(row.totalPrice).toLocaleString() }}
                  </td>
                </tr>
                <tr v-if="unseenOrderIds.length > 0">
                  <td colspan="7" class="px-4 py-3 text-center border-b border-slate-100">
                    <button
                      type="button"
                      @click="markAsSeen"
                      :disabled="markingSeen"
                      class="px-6 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-sm hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-60"
                    >
                      {{ markingSeen ? 'Saving...' : 'Seen' }}
                    </button>
                  </td>
                </tr>
              </template>

              <tr v-else-if="row.type === 'spacer'" class="h-6 py-2 bg-[#f8fafc]/50">
                <td colspan="7"></td>
              </tr>
            </template>
          </template>

          <template v-else-if="filters.reportType === 'Detail'">
            <template v-for="(row, index) in rows" :key="index">
              <tr 
                v-if="row.type === 'data'" 
                class="border-b last:border-0 hover:bg-slate-50 transition-colors"
              >
                <td class="px-3 py-3 text-slate-500 text-[11px]">{{ ((pagination.page - 1) * pagination.limit) + index + 1 }}</td>
                <td class="px-3 py-3 text-left text-slate-600 text-[11px]">{{ formatTime(row.time) }}</td>
                <td class="px-3 py-3 text-left text-slate-600 text-[11px]">{{ row.department }}</td>
                <td class="px-3 py-3 text-left text-slate-600 text-[11px]">{{ row.subDepartment }}</td>
                <td class="px-3 py-3 text-left font-semibold text-[#0d4169] text-[11px]">{{ row.menuName }}</td>
                <td class="px-3 py-3 text-center font-bold text-slate-600 text-[11px]">{{ row.quantity }}</td>
                <td class="px-3 py-3 text-right text-slate-600 text-[11px]">{{ Number(row.unitPrice).toLocaleString() }}</td>
                <td class="px-3 py-3 text-right font-bold text-[#0d4169] text-[11px]">{{ Number(row.totalPrice).toLocaleString() }}</td>
              </tr>

              <tr v-else-if="row.type === 'subtotal'" class="border-b bg-emerald-50/30 font-bold">
                <td colspan="5" class="px-3 py-2 text-right text-emerald-600 uppercase font-bold text-[10px]">{{ row.date }} TOTAL</td>
                <td class="px-3 py-2 border-b border-emerald-600/20"></td>
                <td class="px-3 py-2 border-b border-emerald-600/20"></td>
                <td class="px-3 py-2 text-right font-black text-emerald-600 text-[12px] border-b border-emerald-600/20">{{ Number(row.totalPrice).toLocaleString() }}</td>
              </tr>

              <template v-else-if="row.type === 'total'">
                <!-- Payment Method Breakdown (only for All Methods) -->
                <template v-if="filters.paymentMethodId === null && pagination.paymentMethodTotals?.length">
                  <tr v-for="(pm, pmIdx) in pagination.paymentMethodTotals" :key="pm.name">
                    <td 
                      colspan="7" 
                      :class="['px-4 py-2 text-right text-slate-500 font-bold uppercase text-[10px]', pmIdx === 0 ? 'border-t-2 border-emerald-700/30' : '']"
                    >
                      {{ pm.name }}:
                    </td>
                    <td :class="['px-4 py-2 text-right font-bold text-slate-600 text-[11px]', pmIdx === 0 ? 'border-t-2 border-emerald-700/30' : '']">
                      {{ Number(pm.amount).toLocaleString() }}
                    </td>
                  </tr>
                </template>

                <tr>
                  <td 
                    colspan="7" 
                    :class="[
                      'px-3 py-4 text-right text-emerald-700 font-black uppercase text-[11px] tracking-widest',
                      (filters.paymentMethodId !== null || !pagination.paymentMethodTotals?.length) ? 'border-t-2 border-emerald-700/30' : ''
                    ]"
                  >
                    GRAND TOTAL
                  </td>
                  <td 
                    :class="[
                      'px-3 py-4 text-right font-black text-emerald-700 text-lg',
                      (filters.paymentMethodId !== null || !pagination.paymentMethodTotals?.length) ? 'border-t-2 border-emerald-700/30' : ''
                    ]"
                  >
                    {{ Number(row.totalPrice).toLocaleString() }}
                  </td>
                </tr>
                <tr v-if="unseenOrderIds.length > 0">
                  <td colspan="8" class="px-3 py-3 text-center border-b border-slate-100">
                    <button
                      type="button"
                      @click="markAsSeen"
                      :disabled="markingSeen"
                      class="px-6 py-1.5 bg-emerald-600 text-white text-sm font-bold rounded-md shadow-sm hover:bg-emerald-700 transition-all active:scale-95 disabled:opacity-60"
                    >
                      {{ markingSeen ? 'Saving...' : 'Seen' }}
                    </button>
                  </td>
                </tr>
              </template>
            </template>
          </template>

          <!-- Management Rows (New) -->
          <template v-else-if="filters.reportType === 'Management'">
            <tr 
              v-for="(row, index) in rows" 
              :key="index"
              class="border-b last:border-0 hover:bg-slate-50 transition-colors"
            >
              <td class="px-3 py-3 text-slate-500 text-[11px]">{{ ((pagination.page - 1) * pagination.limit) + index + 1 }}</td>
              <td class="px-3 py-3 text-left text-slate-600 text-[11px]">{{ formatFullDateTime(row.time) }}</td>
              <td class="px-3 py-3 text-left text-slate-600 text-[11px]">{{ row.department }}</td>
              <td class="px-3 py-3 text-left text-slate-600 text-[11px]">{{ row.subDepartment }}</td>
              <td class="px-3 py-3 text-left font-semibold text-[#0d4169] text-[11px]">{{ row.menuName }}</td>
              <td class="px-3 py-3 text-center font-bold text-slate-600 text-[11px]">{{ row.quantity }}</td>
              <td class="px-3 py-3 text-left font-medium text-emerald-700 text-[11px]">{{ row.waiterName }}</td>
            </tr>

            <!-- Management Grand Total Row -->
            <!-- <tr class="bg-emerald-50/20 font-bold border-t-2 border-emerald-700/10">
              <td colspan="5" class="px-3 py-4 text-right text-emerald-700 font-black uppercase text-[11px] tracking-widest">
                TOTAL MANAGEMENT QUANTITY
              </td>
              <td class="px-3 py-4 text-center font-black text-emerald-700 text-sm">
                {{ rows.reduce((acc, r) => acc + (r.quantity || 0), 0) }}
              </td>
              <td></td>
            </tr> -->
          </template>
        </tbody>
      </table>

        <!-- Pagination Controls -->
        <div v-if="pagination.totalPages > 1" class="mt-6 flex flex-wrap items-center justify-center gap-2 pb-10">
          <button 
            @click="generateReport(1)" 
            :disabled="pagination.page === 1"
            class="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed font-bold"
          >
            «
          </button>
          <button 
            @click="generateReport(pagination.page - 1)" 
            :disabled="pagination.page === 1"
            class="px-4 h-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold"
          >
            Prev
          </button>

          <div class="flex items-center gap-1">
            <button 
              v-for="p in pagination.totalPages" 
              :key="p"
              @click="generateReport(p)"
              :class="[
                'w-10 h-10 flex items-center justify-center rounded-lg border transition-all text-sm font-bold',
                pagination.page === p ? 'bg-[#0d4169] text-white border-[#0d4169] shadow-lg scale-110' : 'bg-white text-slate-600 border-slate-200 hover:border-[#0d4169] hover:text-[#0d4169]'
              ]"
            >
              {{ p }}
            </button>
          </div>

          <button 
            @click="generateReport(pagination.page + 1)" 
            :disabled="pagination.page === pagination.totalPages"
            class="px-4 h-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm font-bold"
          >
            Next
          </button>
          <button 
            @click="generateReport(pagination.totalPages)" 
            :disabled="pagination.page === pagination.totalPages"
            class="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-40 disabled:cursor-not-allowed font-bold"
          >
            »
          </button>
        </div>
      </div>
    </div>

    <!-- Unseen Orders Modal -->
    <div
      v-if="showUnseenOrdersModal"
      class="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      @click.self="closeUnseenOrdersModal"
    >
      <div class="bg-white rounded-xl shadow-xlborder border-[#0d4169] w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden pt-0 mt-0">
        <div class="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-[#0d4169] text-white">
          <h3 class="text-base font-bold">Unseen Orders</h3>
          <button
            type="button"
            @click="closeUnseenOrdersModal"
            class="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 text-white text-lg leading-none"
          >
            &times;
          </button>
        </div>

        <div class="flex-1 overflow-auto px-4 py-4">
          <div v-if="loadingUnseenList" class="py-16 text-center text-sm text-[#0d4169]">
            <span class="inline-block w-5 h-5 border-2 border-[#0d4169] border-t-transparent rounded-full animate-spin mr-2 align-middle"></span>
            Loading orders...
          </div>
          <div v-else-if="unseenOrdersList.length === 0" class="py-16 text-center text-slate-400 italic text-sm">
            No unseen paid orders found.
          </div>
          <div v-else class="overflow-x-auto">
            <table class="w-full border-collapse bg-white">
              <thead class="sticky top-0 z-10 bg-[#f8fafc]">
                <tr>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">No</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">Created at</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">Confirmed at</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">Created by</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">Confirmed by</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">From</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">For Whom</th>
                  <th class="px-3 py-2 text-left font-bold text-[#64748b] border-b text-[11px]">Menu Name</th>
                  <th class="px-3 py-2 text-center font-bold text-[#64748b] border-b text-[11px]">Quantity</th>
                  <th class="px-3 py-2 text-right font-bold text-[#64748b] border-b text-[11px]">Unit Price</th>
                  <th class="px-3 py-2 text-right font-bold text-[#64748b] border-b text-[11px]">Total Price</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in unseenOrdersList"
                  :key="row.id"
                  class="border-b last:border-0 hover:bg-slate-50 transition-colors"
                >
                  <td class="px-3 py-2 text-slate-500 text-[11px]">{{ row.no }}</td>
                  <td class="px-3 py-2 text-slate-600 text-[11px]">{{ formatFullDateTime(row.createdAt) }}</td>
                  <td class="px-3 py-2 text-slate-600 text-[11px]">{{ formatFullDateTime(row.confirmedAt) }}</td>
                  <td class="px-3 py-2 text-slate-600 text-[11px]">{{ row.createdBy }}</td>
                  <td class="px-3 py-2 text-slate-600 text-[11px]">{{ row.confirmedBy }}</td>
                  <td class="px-3 py-2 text-slate-600 text-[11px]">{{ formatFromWhere(row.fromWhere) }}</td>
                  <td class="px-3 py-2 text-slate-600 text-[11px]">{{ formatIsForStaff(row.isForStaff) }}</td>
                  <td class="px-3 py-2 font-semibold text-[#0d4169] text-[11px]">{{ row.menuName }}</td>
                  <td class="px-3 py-2 text-center font-bold text-slate-600 text-[11px]">{{ row.quantity }}</td>
                  <td class="px-3 py-2 text-right text-slate-600 text-[11px]">{{ Number(row.unitPrice).toLocaleString() }}</td>
                  <td class="px-3 py-2 text-right font-bold text-[#0d4169] text-[11px]">{{ Number(row.totalPrice).toLocaleString() }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="px-6 py-3 border-t border-slate-100 flex justify-end">
          <button
            type="button"
            @click="closeUnseenOrdersModal"
            class="px-6 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)

const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)

const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const loading = ref(false)
const markingSeen = ref(false)
const lastPosOrderCreatedAt = ref(null)
const unseenOrderIds = ref([])
const unseenPaidOrdersCount = ref(0)
const showUnseenOrdersModal = ref(false)
const loadingUnseenList = ref(false)
const unseenOrdersList = ref([])
const paymentMethods = ref([])
const cashiers = ref([])
const allUsers = ref([])
const managementStaffUsers = ref([])
const departments = ref([])
const subDepartments = ref([])
const deliveryCompanies = ref([])
const rows = ref([])
const pagination = ref({
  page: 1,
  limit: 15,
  totalCount: 0,
  totalPages: 0,
  grandTotal: 0,
  paymentMethodTotals: []
})

const filters = ref({
  startDate: '',
  endDate: '',
  createdDate: '',
  reportType: 'Summary',
  paymentMethodId: null,
  orderType: 'All',
  deliveryCompanyId: null,
  departmentId: null,
  subDepartmentId: null,
  waiterId: null,
  cashierId: null,
  managementUserId: null,
  menuName: ''
})

const canPrintReport = computed(() =>
  !loading.value &&
  !!filters.value.startDate &&
  rows.value.length > 0 &&
  (filters.value.reportType === 'Summary' || filters.value.reportType === 'Detail')
)

function formatFullDateTime(dateStr) {
  if (!dateStr) return '---'
  const date = new Date(dateStr)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`
}

function formatLastSyncDateTime(dateStr) {
  if (!dateStr) return '---'
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return '---'
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  const hh = String(date.getHours()).padStart(2, '0')
  const min = String(date.getMinutes()).padStart(2, '0')
  const ss = String(date.getSeconds()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`
}

const lastPosSyncDisplay = computed(() => formatLastSyncDateTime(lastPosOrderCreatedAt.value))

async function loadLatestPosOrderCreatedAt() {
  try {
    const params = new URLSearchParams()
    if (companyId.value) params.set('companyId', companyId.value)
    if (branchId.value) params.set('branchId', branchId.value)
    if (![...params.keys()].length) {
      lastPosOrderCreatedAt.value = null
      return
    }

    const res = await apiFetch(`/orders/latest-pos-created-at?${params.toString()}`)
    if (!res.ok) {
      lastPosOrderCreatedAt.value = null
      return
    }
    const data = await res.json()
    lastPosOrderCreatedAt.value = data?.createdAt || null
  } catch (err) {
    console.error('Failed to load latest POS order createdAt', err)
    lastPosOrderCreatedAt.value = null
  }
}

function toLocalISODate(date = new Date()) {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function formatTime(dateStr) {
  if (!dateStr) return '---'
  const date = new Date(dateStr)
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function formatFromWhere(value) {
  if (!value) return '---'
  const key = String(value).toLowerCase()
  if (key === 'pos') return 'POS'
  if (key === 'qr') return 'QR'
  return String(value).toUpperCase()
}

function formatIsForStaff(value) {
  if (!value) return '---'
  const key = String(value).toLowerCase()
  if (key === 'staff') return 'Staff'
  if (key === 'normal') return 'Normal'
  return String(value)
}

function formatDateForDisplay(isoDate) {
  const s = String(isoDate || '')
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return s
  return `${m[3]}/${m[2]}/${m[1]}`
}

function getReportDateRangeLabel() {
  const from = filters.value.startDate
  if (!from) return '-'
  const fromLabel = formatDateForDisplay(from)
  const to = filters.value.endDate
  return to ? `${fromLabel} - ${formatDateForDisplay(to)}` : fromLabel
}

function getImageFormatFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'PNG'
  if (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg')) return 'JPEG'
  if (dataUrl.startsWith('data:image/webp')) return 'WEBP'
  return 'PNG'
}

async function imageUrlToDataUrl(url) {
  if (!url) return { dataUrl: null, format: 'PNG' }
  try {
    const res = await fetch(url)
    if (!res.ok) return { dataUrl: null, format: 'PNG' }
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return {
      dataUrl,
      format: getImageFormatFromDataUrl(dataUrl)
    }
  } catch {
    return { dataUrl: null, format: 'PNG' }
  }
}

function buildReportQueryParams(pageNumber = 1, limit = pagination.value.limit) {
  const queryParams = { ...filters.value, page: pageNumber, limit }
  if (companyId.value) queryParams.companyId = companyId.value
  if (branchId.value) queryParams.branchId = branchId.value

  if (queryParams.paymentMethodId === 'Delivery') {
    queryParams.orderType = 'Delivery'
    delete queryParams.paymentMethodId
  }

  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === null || queryParams[key] === '' || queryParams[key] === 'All') {
      delete queryParams[key]
    }
  })

  const isManagementActive = filters.value.managementUserId !== null
  const isForStaffParam = isManagementActive ? 'Staff' : 'Normal'

  let finalWaiterId = filters.value.waiterId
  if (isManagementActive) {
    finalWaiterId = filters.value.managementUserId > 0 ? filters.value.managementUserId : ''
  }

  return new URLSearchParams({
    ...queryParams,
    isForStaff: isForStaffParam,
    waiterId: finalWaiterId || ''
  }).toString()
}

async function fetchAllReportRowsForPrint() {
  const totalCount = Number(pagination.value.totalCount || 0)
  const limit = totalCount > 0 ? totalCount : 999999
  const apiQuery = buildReportQueryParams(1, limit)
  const res = await apiFetch(`/orders/report?${apiQuery}`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to load report data for print')
  }
  const data = await res.json()
  return {
    rows: Array.isArray(data.rows) ? data.rows : [],
    grandTotal: Number(data.grandTotal || 0),
    paymentMethodTotals: Array.isArray(data.paymentMethodTotals) ? data.paymentMethodTotals : []
  }
}

async function generatePosReportPdf() {
  const reportType = filters.value.reportType
  if (reportType !== 'Summary' && reportType !== 'Detail') return

  const { rows: printRows, grandTotal, paymentMethodTotals } = await fetchAllReportRowsForPrint()
  if (!printRows.length) return

  const isSummary = reportType === 'Summary'
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const leftMargin = 10
  const rightMargin = 8
  const topMargin = 8
  const headerBottomY = 28
  const totalPagesExp = '{total_pages_count_string}'
  const printDateLabel = getReportDateRangeLabel()
  const { dataUrl: logoDataUrl, format: logoFormat } = await imageUrlToDataUrl(String(printCompanyLogo.value || '').trim())

  let dataNo = 0
  const tableBody = []
  for (const row of printRows) {
    if (row.type === 'data') {
      dataNo += 1
      if (isSummary) {
        tableBody.push([
          String(dataNo),
          row.department || '-',
          row.subDepartment || '-',
          row.menuName || '-',
          String(row.quantity ?? '-'),
          Number(row.unitPrice || 0).toFixed(2),
          Number(row.totalPrice || 0).toFixed(2)
        ])
      } else {
        tableBody.push([
          String(dataNo),
          row.time ? formatTime(row.time) : '-',
          row.department || '-',
          row.subDepartment || '-',
          row.menuName || '-',
          String(row.quantity ?? '-'),
          Number(row.unitPrice || 0).toFixed(2),
          Number(row.totalPrice || 0).toFixed(2)
        ])
      }
    } else if (row.type === 'subtotal') {
      if (isSummary) {
        tableBody.push([
          { content: `${row.department || ''} TOTAL`, colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: Number(row.totalPrice || 0).toFixed(2), styles: { halign: 'right', fontStyle: 'bold' } }
        ])
      } else {
        tableBody.push([
          { content: `${row.date || ''} TOTAL`, colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: Number(row.totalPrice || 0).toFixed(2), styles: { halign: 'right', fontStyle: 'bold' } }
        ])
      }
    }
  }

  const head = isSummary
    ? [['#', 'Department', 'Sub Department', 'Menu Name', 'Quantity', 'Unit Price', 'Total Price']]
    : [['#', 'Date', 'Department', 'Sub Department', 'Menu Name', 'Quantity', 'Unit Price', 'Total Price']]

  autoTable(doc, {
    head,
    body: tableBody,
    startY: headerBottomY + 2,
    theme: 'grid',
    margin: { left: leftMargin, right: rightMargin, top: headerBottomY + 2, bottom: 12 },
    styles: {
      fontSize: 7.5,
      cellPadding: 1.0,
      textColor: [0, 0, 0],
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [0, 0, 0],
      fontStyle: 'bold'
    },
    bodyStyles: {
      fillColor: [255, 255, 255]
    },
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    didDrawPage: () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, logoFormat, leftMargin, topMargin, 14, 10, undefined, 'FAST')
      }
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(10)
      doc.text(String(printCompanyName.value || 'Company'), pageWidth / 2, topMargin + 3, { align: 'center' })
      doc.setFontSize(11)
      doc.text(`POS Report (${reportType})`, pageWidth / 2, topMargin + 8, { align: 'center' })

      doc.setFont('helvetica', 'bold')
      doc.setFontSize(8)
      doc.text(`Date: ${printDateLabel}`, pageWidth - rightMargin, topMargin + 6, { align: 'right' })

      doc.setDrawColor(0, 0, 0)
      doc.setLineWidth(0.2)
      doc.line(leftMargin, headerBottomY, pageWidth - rightMargin, headerBottomY)

      const pageNo = doc.getNumberOfPages()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.text(`Page ${pageNo} of ${totalPagesExp}`, pageWidth - rightMargin, pageHeight - 5, { align: 'right' })
    }
  })

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const finalY = (doc.lastAutoTable?.finalY || (headerBottomY + 2)) + 8
  const valueX = pageWidth - rightMargin
  const labelX = valueX - 40
  const lineGap = 6
  let totalsY = finalY

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)

  const showPaymentBreakdown = filters.value.paymentMethodId === null && paymentMethodTotals.length > 0
  if (showPaymentBreakdown) {
    for (const pm of paymentMethodTotals) {
      doc.text(`${pm.name}:`, labelX, totalsY, { align: 'right' })
      doc.text(Number(pm.amount || 0).toFixed(2), valueX, totalsY, { align: 'right' })
      totalsY += lineGap
    }
  }

  doc.setLineWidth(0.2)
  doc.line(labelX - 18, totalsY - (lineGap * 0.5), valueX, totalsY - (lineGap * 0.5))
  doc.setFont('helvetica', 'bold')
  doc.text('Grand total:', labelX, totalsY, { align: 'right' })
  doc.text(Number(grandTotal || 0).toFixed(2), valueX, totalsY, { align: 'right' })

  const footerY = Math.max(totalsY + (lineGap * 4), pageHeight - 28)
  const firstX = leftMargin + 10
  const secondX = pageWidth / 2 - 20
  const thirdX = pageWidth - rightMargin - 56
  const lineW = 40

  doc.setFont('helvetica', 'normal')
  doc.line(firstX, footerY, firstX + lineW, footerY)
  doc.text('Requested By', firstX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(secondX, footerY, secondX + lineW, footerY)
  doc.text('Checked By', secondX + (lineW / 2), footerY + 5, { align: 'center' })
  doc.line(thirdX, footerY, thirdX + lineW, footerY)
  doc.text('Approved By', thirdX + (lineW / 2), footerY + 5, { align: 'center' })

  const safeDate = String(new Date().toISOString().slice(0, 10)).replace(/[\\/:*?"<>|]/g, '-').trim()
  const filename = `POS-Report-${reportType}-${safeDate}.pdf`
  doc.save(filename)
}

async function handlePrintReport() {
  if (!canPrintReport.value) return
  if (!confirm('Are you sure..........Print?')) return
  try {
    await generatePosReportPdf()
  } catch (error) {
    console.error('Print report error:', error)
    alert(error.message || 'Failed to print report')
  }
}

async function generateReport(pageNumber = 1) {
  if (!filters.value.startDate) return
  loading.value = true
  try {
    const apiQuery = buildReportQueryParams(pageNumber, pagination.value.limit)

    const res = await apiFetch(`/orders/report?${apiQuery}`)
    
    if (res.ok) {
      const data = await res.json()
      rows.value = data.rows
      unseenOrderIds.value = Array.isArray(data.unseenOrderIds) ? data.unseenOrderIds : []
      pagination.value = {
        ...pagination.value,
        page: data.page,
        totalPages: data.totalPages,
        totalCount: data.totalCount,
        grandTotal: data.grandTotal,
        paymentMethodTotals: data.paymentMethodTotals
      }
    } else {
      unseenOrderIds.value = []
      const err = await res.json()
      alert(err.message || 'Failed to generate report')
    }
  } catch (error) {
    console.error('Report Error:', error)
    unseenOrderIds.value = []
    alert('An error occurred while generating the report')
  } finally {
    loading.value = false
  }
}

async function markAsSeen() {
  if (!unseenOrderIds.value.length) return

  markingSeen.value = true
  try {
    const params = new URLSearchParams()
    if (companyId.value) params.set('companyId', companyId.value)
    if (branchId.value) params.set('branchId', branchId.value)

    const res = await apiFetch(`/orders/mark-seen?${params.toString()}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderIds: unseenOrderIds.value })
    })

    if (res.ok) {
      unseenOrderIds.value = []
      await loadUnseenPaidOrdersCount()
    } else {
      const err = await res.json()
      alert(err.message || 'Failed to mark orders as seen')
    }
  } catch (error) {
    console.error('Mark seen error:', error)
    alert('An error occurred while marking orders as seen')
  } finally {
    markingSeen.value = false
  }
}

function buildUnseenOrdersQueryParams() {
  const params = new URLSearchParams()
  if (companyId.value) params.set('companyId', companyId.value)
  if (branchId.value) params.set('branchId', branchId.value)
  return params
}

async function loadUnseenPaidOrdersCount() {
  if (!companyId.value && !branchId.value) {
    unseenPaidOrdersCount.value = 0
    return
  }
  try {
    const res = await apiFetch(`/orders/unseen-paid-count?${buildUnseenOrdersQueryParams().toString()}`)
    if (res.ok) {
      const data = await res.json()
      unseenPaidOrdersCount.value = Number(data.count) || 0
    }
  } catch (err) {
    console.error('Failed to load unseen paid orders count', err)
  }
}

async function openUnseenOrdersModal() {
  if (!companyId.value && !branchId.value) return

  showUnseenOrdersModal.value = true
  loadingUnseenList.value = true
  unseenOrdersList.value = []

  try {
    const res = await apiFetch(`/orders/unseen-paid-list?${buildUnseenOrdersQueryParams().toString()}`)
    if (res.ok) {
      const data = await res.json()
      unseenOrdersList.value = Array.isArray(data.rows) ? data.rows : []
    } else {
      const err = await res.json()
      alert(err.message || 'Failed to load unseen orders')
      showUnseenOrdersModal.value = false
    }
  } catch (err) {
    console.error('Failed to load unseen orders list', err)
    alert('An error occurred while loading unseen orders')
    showUnseenOrdersModal.value = false
  } finally {
    loadingUnseenList.value = false
  }
}

function closeUnseenOrdersModal() {
  showUnseenOrdersModal.value = false
  unseenOrdersList.value = []
}

async function loadPMs() {
  try {
    const res = await apiFetch('/settings/payment-methods')
    if (res.ok) {
      const data = await res.json()
      paymentMethods.value = data.filter(pm => pm.isActive === 1)
    }
  } catch (err) {
    console.error('Failed to load payment methods', err)
  }
}

async function loadUsers() {
  if (!companyId.value && !branchId.value) return
  try {
    let url = ''
    if (branchId.value) url = `users/branch/${branchId.value}`
    else if (companyId.value) url = `users/company/${companyId.value}`

    const res = await apiFetch(url)
    if (res.ok) {
      allUsers.value = await res.json()
      allUsers.value.sort((a, b) =>
        (a.full_name || '').localeCompare(b.full_name || '')
      )
    }
  } catch (err) {
    console.error('Failed to load users', err)
  }
}

async function loadCashiers() {
  if (!companyId.value && !branchId.value) {
    cashiers.value = []
    return
  }
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.set('branchId', branchId.value)
    else if (companyId.value) params.set('companyId', companyId.value)

    const res = await apiFetch(`/orders/confirmation-users?${params.toString()}`)
    if (res.ok) {
      cashiers.value = await res.json()
    }
  } catch (err) {
    console.error('Failed to load cashiers', err)
  }
}

async function loadManagementStaffUsers() {
  if (!companyId.value && !branchId.value) {
    managementStaffUsers.value = []
    return
  }
  try {
    const params = new URLSearchParams()
    if (branchId.value) params.set('branchId', branchId.value)
    else if (companyId.value) params.set('companyId', companyId.value)

    const res = await apiFetch(`/orders/management-staff-users?${params.toString()}`)
    if (res.ok) {
      managementStaffUsers.value = await res.json()
    }
  } catch (err) {
    console.error('Failed to load management staff users', err)
  }
}

async function loadDepartments() {
  if (!companyId.value) return
  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) endpoint = `/departments/branch/${branchId.value}`
    
    const res = await apiFetch(endpoint)
    if (res.ok) {
      departments.value = await res.json()
    }
  } catch (err) {
    console.error('Failed to load departments', err)
  }
}

async function loadSubDepartments() {
  if (!filters.value.departmentId) {
    subDepartments.value = []
    return
  }
  try {
    const res = await apiFetch(`/sub-departments/department/${filters.value.departmentId}`)
    if (res.ok) {
      subDepartments.value = await res.json()
    }
  } catch (err) {
    console.error('Failed to load sub-departments', err)
  }
}

async function loadDeliveryCompanies() {
  try {
    const res = await apiFetch('/settings/delivery-companies')
    if (res.ok) {
      const data = await res.json()
      deliveryCompanies.value = data.filter(c => c.isActive === 1)
    }
  } catch (err) {
    console.error('Failed to load delivery companies', err)
  }
}


watch(() => filters.value.departmentId, () => {
  filters.value.subDepartmentId = null
  loadSubDepartments()
})

onMounted(() => {
  filters.value.startDate = toLocalISODate()
  loadPMs()
  loadUsers()
  loadManagementStaffUsers()
  loadCashiers()
  loadDepartments()
  loadDeliveryCompanies()
  loadUnseenPaidOrdersCount()
  loadLatestPosOrderCreatedAt()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 12px;
  line-height: 1.2;
  color: #1e293b;
}

th, td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 8px 12px;
}

th {
  background: #f8fafc;
  cursor: default;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.025em;
}

/* For better form control appearance */
select, input {
  font-family: inherit;
}
</style>
